import React, { useState } from "react";
import GenericModal from "@ui-components/Modal/GenericModal";
import GenericDropdown from "@ui-components/Dropdown/GenericDropdown";
import GenericInput from "@ui-components/Input/GenericInput";
import GenericSwitch from "@ui-components/Switch/GenericSwitch";
import GenericButton from "@ui-components/Button/GenericButton";
import GenericTooltip from "@ui-components/Tooltip/GenericTooltip";
import GenericAlert from "@ui-components/Alert/GenericAlert";
import { useProcessingOverlay } from "@hooks/useProcessingOverlay";
import { useAxiosAuth0 } from "@axios-config/useAxiosAuth0";

import { useAppSelector } from "@redux/hooks";
// Import your request methods
import { createKontrolTicket, createTicket } from "../request/request";

interface LabelValue {
	label: string;
	value: string;
}

interface FeatureRequestModalProps {
	isOpen: boolean;
	onClose: () => void;
	onTicketCreated?: () => void;
}

// We have two products: CTX Reflector and Kontrol Edge
const productData: LabelValue[] = [
	{ label: "CTX Reflector", value: "ctx-r" },
	{ label: "Kontrol Edge", value: "ke" },
];

const countryCodes: LabelValue[] = [
	{ label: "United States (+1)", value: "+1" },
	{ label: "Mexico (+52)", value: "+52" },
	{ label: "Canada (+1)", value: "+1" },
];

// We define all possible tabs, but we will *conditionally* use them
const ALL_TABS = [
	{ id: "generalInfo", label: "General Info" },
	{ id: "attachments", label: "Attachments & Response" },
];

export const FeatureRequestModal: React.FC<FeatureRequestModalProps> = ({
	isOpen,
	onClose,
	onTicketCreated,
}) => {
	const api = useAxiosAuth0();

	// ----- FORM STATE -----
	const [selectedProductLabel, setSelectedProductLabel] = useState("");
	// Cloud/region are fixed if needed
	const [cloud] = useState("CloudGlobal");
	const [region] = useState("us-east-1");
	const [title, setTitle] = useState("");
	const [description, setDescription] = useState("");

	// Additional contact info
	const [sendResponse, setSendResponse] = useState(false);
	const [contactName, setContactName] = useState("");
	const [contactEmail, setContactEmail] = useState("");
	const [selectedCountryCode, setSelectedCountryCode] = useState("+1");
	const [contactPhoneNumber, setContactPhoneNumber] = useState("");

	// Attachments
	const [attachmentFiles, setAttachmentFiles] = useState<FileList | null>(null);

	// ----- UI CONTROL -----
	const [activeTab, setActiveTab] = useState<string>("generalInfo");
	const [alertOpen, setAlertOpen] = useState(false);
	const [alertVariant, setAlertVariant] = useState<"success" | "error">(
		"success"
	);
	const [alertMessage, setAlertMessage] = useState("");
	const [isSaving, setIsSaving] = useState(false);

	const isLoadingSomething = isSaving;
	const overlay = useProcessingOverlay(isLoadingSomething);

	// Check if the selected product is "Kontrol Edge"
	const globalProduct = useAppSelector(
		(state) => state.product.selectedProduct
	);
	const isKontrolEdge = globalProduct === "Kontrol Edge";

	// -------------------------------------------------------------------
	// 1) If "Kontrol Edge" is selected => skip the "attachments" tab
	// -------------------------------------------------------------------
	// So, we define TABS dynamically:
	const TABS = isKontrolEdge
		? ALL_TABS.filter((tab) => tab.id === "generalInfo") // Only keep the "generalInfo" tab
		: ALL_TABS; // Otherwise, keep both

	// Basic validation helpers
	const stripNonAlphanumeric = (str: string) => str.replace(/[\s\W_]+/g, "");
	const isValidEmail = (email: string) => {
		const pattern = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
		return pattern.test(email);
	};

	// --- Validation logic for tabs ---
	const isGeneralInfoValid = () => {
		// For Kontrol Edge, we only require product to be selected
		if (isKontrolEdge) {
			return true;
		}
		// Otherwise, we require product, plus some minimal checks on title & description
		const titleClean = stripNonAlphanumeric(title);
		const descClean = stripNonAlphanumeric(description);
		return titleClean.length >= 5 && descClean.length >= 10;
	};

	const isAttachmentsValid = () => {
		// If Kontrol Edge => we skip attachments => always valid
		if (isKontrolEdge) return true;
		// If not sending response => skip validation
		if (!sendResponse) return true;
		// Otherwise require name >= 3 and a valid email
		const minName = contactName.trim().length >= 3;
		const validEmail = isValidEmail(contactEmail.trim());
		return minName && validEmail;
	};

	const canActivateTab = (tabId: string) => {
		switch (tabId) {
			case "generalInfo":
				return true;
			case "attachments":
				return isGeneralInfoValid();
			default:
				return false;
		}
	};

	const handleChangeTab = (tabId: string) => {
		if (canActivateTab(tabId)) {
			setActiveTab(tabId);
		}
	};

	// ----- EVENT HANDLERS -----
	const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
		if (e.target.files) {
			setAttachmentFiles(e.target.files);
		}
	};

	const handleCloseModal = () => {
		resetFormState();
		onClose();
	};

	const resetFormState = () => {
		setSelectedProductLabel("");
		setTitle("");
		setDescription("");
		setSendResponse(false);
		setContactName("");
		setContactEmail("");
		setSelectedCountryCode("+1");
		setContactPhoneNumber("");
		setAttachmentFiles(null);
		setActiveTab("generalInfo");
	};

	// ----- SUBMIT / SAVE -----
	const handleSave = async () => {
		if (!isAttachmentsValid()) return;

		setIsSaving(true);
		try {
			// Convert label => value
			const foundProduct = productData.find(
				(p) => p.label === selectedProductLabel
			);
			const productValue = foundProduct ? foundProduct.value : "";

			if (isKontrolEdge) {
				// If Kontrol Edge => minimal fields => createKontrolTicket
				const kontrolPayload = {
					requesttype: "FEATURE_REQUEST",
					summary: title,
					description,
				};
				await createKontrolTicket(api, kontrolPayload);
				if (onTicketCreated) {
					onTicketCreated();
				}
			} else {
				// Otherwise => normal Feature Request => createTicket
				const finalPayload = {
					Tipo: "Feature Request",
					product: productValue,
					cloud,
					region,
					Resumen: title,
					Descripcion: description,
					files: attachmentFiles ? Array.from(attachmentFiles) : [],
					Nombre_de_contacto: contactName,
					Correo: contactEmail,
					phone_number: `${selectedCountryCode}${contactPhoneNumber.replace(
						/\s+/g,
						""
					)}`,
				};
				const response = await createTicket(api, finalPayload);
				console.log("Feature Request ticket created =>", response.data);
			}
			setAlertVariant("success");
			setAlertMessage("¡Feature Request created successfully!");
			setAlertOpen(true);
		} catch (err) {
			console.error("Error creating feature request =>", err);
			setAlertVariant("error");
			setAlertMessage(
				"A problem occurred while creating the feature request, please try again."
			);
			setAlertOpen(true);
		} finally {
			setIsSaving(false);
		}
	};

	// We show "Next" if there's more than one tab, otherwise "Save"
	// i.e. if isKontrolEdge => we only have 1 tab => show "Save"
	const isLastTab = () => {
		// If we have only 1 tab in TABS => obviously it's the last tab
		if (TABS.length === 1) return true;
		// Otherwise we check if the active tab is the last in the array
		return TABS[TABS.length - 1].id === activeTab;
	};

	// We can conditionally render the bottom button in the general info step:
	// If it's the last tab => "Save" else => "Next"
	// For the attachments step => always "Save"
	// We can also skip the attachments step if Kontrol Edge => no "Next" needed.
	const renderFooterButtons = () => {
		if (activeTab === "generalInfo") {
			if (isLastTab()) {
				// It's the only tab => show "Save"
				return (
					<div className="flex justify-end pt-2">
						<GenericButton
							label="Save"
							variant="brand"
							onClick={handleSave}
							disabled={!isGeneralInfoValid()}
						/>
					</div>
				);
			} else {
				// There's another tab => show "Next"
				return (
					<div className="flex justify-end pt-2">
						<GenericButton
							label="Next"
							variant="brand"
							onClick={() => handleChangeTab("attachments")}
							disabled={!isGeneralInfoValid()}
						/>
					</div>
				);
			}
		} else if (activeTab === "attachments") {
			// If we do have an attachments tab => show "Back" + "Save"
			return (
				<div className="flex justify-end pt-2 gap-2">
					<GenericButton
						label="Back"
						variant="secondary"
						onClick={() => setActiveTab("generalInfo")}
					/>
					<GenericButton
						label="Save"
						variant="brand"
						onClick={handleSave}
						disabled={!isAttachmentsValid() || isSaving}
					/>
				</div>
			);
		}
		return null;
	};

	return (
		<GenericModal
			isOpen={isOpen}
			onClose={handleCloseModal}
			closeOnBackdropClick={false}
			size="lg"
			title={
				<div className="flex items-center gap-2">
					<span>Feature Request</span>
					<GenericTooltip
						text="Use this section to inform us about your Feature requirements."
						position="top"
					/>
				</div>
			}
		>
			{/* Tab navigation */}
			<div className="border-b border-gray-200 mb-4">
				<nav className="flex space-x-4" aria-label="Tabs">
					{TABS.map((tab) => {
						const disabled = !canActivateTab(tab.id);
						return (
							<button
								key={tab.id}
								onClick={() => handleChangeTab(tab.id)}
								disabled={disabled}
								className={`
                  px-3 py-2 text-sm font-medium focus:outline-none
                  ${
										activeTab === tab.id
											? "text-blue-600 border-b-2 border-blue-600"
											: disabled
											? "text-gray-400 cursor-not-allowed"
											: "text-gray-500 hover:text-gray-700"
									}
                `}
							>
								{tab.label}
							</button>
						);
					})}
				</nav>
			</div>

			{/* Step 1: General Info */}
			{activeTab === "generalInfo" && (
				<div className="space-y-4 text-sm text-gray-700">
					{/* Cloud & Region => only if not KontrolEdge */}
					{!isKontrolEdge && (
						<div className="flex gap-4">
							<div className="w-1/2">
								<label className="block mb-1 font-medium">
									Cloud <span className="text-red-500">*</span>
								</label>
								<GenericInput value={cloud} disabled />
							</div>
							<div className="w-1/2">
								<label className="block mb-1 font-medium">
									Region <span className="text-red-500">*</span>
								</label>
								<GenericInput value={region} disabled />
							</div>
						</div>
					)}

					{/* Title */}
					<div>
						<label className="block mb-1 font-medium flex items-center gap-1">
							Title <span className="text-red-500">*</span>
							<GenericTooltip
								text="At least 5 characters (ignoring spaces & punctuation)."
								position="top"
							/>
						</label>
						<GenericInput
							placeholder="Short summary of the request"
							value={title}
							onChange={setTitle}
						/>
					</div>

					{/* Description */}
					<div>
						<label className="block mb-1 font-medium flex items-center gap-1">
							Description <span className="text-red-500">*</span>
							<GenericTooltip
								text="At least 10 characters (ignoring spaces & punctuation)."
								position="top"
							/>
						</label>
						<textarea
							className="w-full h-24 px-3 py-2 border border-gray-300 rounded-sm text-sm"
							placeholder="Describe the feature or enhancement"
							value={description}
							onChange={(e) => setDescription(e.target.value)}
						/>
					</div>

					{/* Conditionally show next or save button */}
					{renderFooterButtons()}
				</div>
			)}

			{/* Step 2: Attachments (only if not KontrolEdge => TABS has 2 items) */}
			{activeTab === "attachments" && (
				<div className="space-y-4 text-sm text-gray-700">
					{/* Attachments */}
					<div>
						<label className="block mb-1 font-medium">
							Attach (screenshots, mockups, etc.)
						</label>
						<div className="flex items-center gap-2">
							<label
								htmlFor="file-upload"
								className="
                  cursor-pointer 
                  px-3 py-2 
                  border border-gray-300 
                  rounded-sm 
                  bg-gray-50
                  text-sm 
                  hover:bg-gray-100
                "
							>
								{attachmentFiles ? "Change Files" : "Choose Files"}
							</label>
							<input
								id="file-upload"
								type="file"
								className="hidden"
								multiple
								onChange={handleFileChange}
							/>
							<span className="text-sm text-gray-500">
								{attachmentFiles
									? Array.from(attachmentFiles)
											.map((file) => file.name)
											.join(", ")
									: "No file chosen"}
							</span>
						</div>
					</div>

					{/* Switch + Contact Info */}
					<div className="space-y-2">
						<div className="flex items-center justify-between mt-3">
							<span className="font-medium text-sm text-gray-800">
								I want to send a response of the case to another person
							</span>
							<GenericSwitch
								checked={sendResponse}
								onChange={setSendResponse}
							/>
						</div>

						{sendResponse && (
							<div className="space-y-4 mt-2 border border-gray-200 rounded p-3">
								<div className="flex gap-4">
									<div className="w-1/2">
										<label className="block mb-1 font-medium flex items-center gap-1">
											Contact Name <span className="text-red-500">*</span>
											<GenericTooltip
												text="At least 3 characters."
												position="top"
											/>
										</label>
										<GenericInput
											placeholder="Name"
											value={contactName}
											onChange={setContactName}
										/>
									</div>
									<div className="w-1/2">
										<label className="block mb-1 font-medium flex items-center gap-1">
											Contact Email <span className="text-red-500">*</span>
											<GenericTooltip
												text="Must be a valid email format."
												position="top"
											/>
										</label>
										<GenericInput
											placeholder="Email"
											value={contactEmail}
											onChange={setContactEmail}
											type="email"
										/>
									</div>
								</div>

								<div className="flex gap-4">
									<div className="w-1/2">
										<label className="block mb-1 font-medium">
											Country Code
										</label>
										<GenericDropdown
											items={countryCodes.map((c) => c.label)}
											value={
												countryCodes.find(
													(c) => c.value === selectedCountryCode
												)?.label || "United States (+1)"
											}
											onSelect={(label) => {
												const found = countryCodes.find(
													(c) => c.label === label
												);
												if (found) {
													setSelectedCountryCode(found.value);
												}
											}}
											defaultLabel="Select country code"
											minWidthClass=""
											maxWidthClass=""
											widthClass="w-full"
										/>
									</div>
									<div className="w-1/2">
										<label className="block mb-1 font-medium">
											Phone Number
										</label>
										<GenericInput
											placeholder="Phone number"
											value={contactPhoneNumber}
											onChange={setContactPhoneNumber}
										/>
									</div>
								</div>
							</div>
						)}
					</div>

					{/* Show "Back" + "Save" buttons */}
					{renderFooterButtons()}
				</div>
			)}

			{overlay}

			<GenericAlert
				isOpen={alertOpen}
				onClose={() => setAlertOpen(false)}
				onConfirm={() => {
					setAlertOpen(false);
					if (alertVariant === "success") {
						handleCloseModal();
					}
				}}
				variant={alertVariant}
				title={alertVariant === "success" ? "Feature Request Created" : "Error"}
				description={alertMessage}
				confirmLabel="OK"
			/>
		</GenericModal>
	);
};

export default FeatureRequestModal;
